import { z } from 'zod';

// BR-23: closed dictionary of event names; ingest accepts 1-50 events/request,
// name max 100 chars, platform ios/android/web, occurredAt defaults to server time.
export const EVENT_NAMES = [
  'waitlist_submitted',
  'onboarding_started',
  'onboarding_completed',
  // Emitted by mobile after a successful sign-up (app/(auth)/sign-in.tsx). Sits
  // between onboarding_started and onboarding_completed in the activation funnel.
  'signup_completed',
  'screen_viewed',
  'button_tapped',
  'assessment_started',
  'assessment_item_answered',
  'assessment_completed',
  'hint_requested',
  'answer_submitted',
  'feedback_generated',
  // PRE-12: mobile emits when the session feedback report screen is shown ready.
  'feedback_viewed',
  'account_delete_requested',
  'account_delete_cancelled',
  // BR-85: admin reset a user's data (kept their sign-in) from the admin Users
  // screen. Recorded under the acting admin (survives the reset) with the target
  // user id.
  'admin_user_data_reset',
  // BR-41: practice session lifecycle events (PRE-16). Emitted by PRE-18 endpoints.
  'session_started',
  'session_paused',
  'session_resumed',
  'session_completed',
  'session_abandoned',
  // BR-35: AI tutor policy (PRE-20). `tutor_replied` per guarded turn;
  // `direct_answer_requested` when the learner asks for the solution (Socratic friction).
  'tutor_replied',
  'direct_answer_requested',
  // BR-57: roadmap lifecycle (PRE-13 generator emits `roadmap_generated`; PRE-8
  // endpoints emit the item start/complete events).
  'roadmap_generated',
  'roadmap_item_started',
  'roadmap_item_completed',
  // BR-67: emitted once when the last item of a roadmap is completed (PRE-129).
  'roadmap_completed',
  // BR-72 (PRE-134): graduation / exit-assessment. `*_started` when a graduation
  // session is opened for a completed roadmap; `*_evaluated` once its result is
  // computed (payload.outcome = 'graduated' | 'reloop').
  'roadmap_graduation_started',
  'roadmap_graduation_evaluated',
  // BR-72 (PRE-150): a graduated learner picked a next step (level_up | new_track)
  // and a fresh roadmap was generated for it.
  'roadmap_graduation_next_generated',
  // BR-36: free-tier daily limit reached (PRE-7).
  'rate_limit_hit',
  // PRE-92 (Phase 8): CV upload + JD target-role lifecycle. `*_parsed` fires on a
  // successful LLM extraction; `*_parse_failed` when the PDF/LLM step fails.
  'cv_uploaded',
  'cv_parsed',
  'cv_parse_failed',
  // PRE-161: skill rating runs after the upload responds, so its outcome can no
  // longer ride on `cv_parsed`. This is where `ratingFallback` lives now — the
  // signal that tells a CV whose skills were never rated from one whose skills
  // were, which is what made the 13 Aug outage look like a normal day.
  'cv_skills_rated',
  'jd_parsed',
  'jd_parse_failed',
  // PRE-95 (Phase 8): a CV↔JD gap analysis was run for a target role and a
  // role-targeted roadmap generated from it.
  'cv_jd_gap_analyzed',
  // BR-80 (PRE-148): daily habit-nudge cron sent a push to the user
  // (payload.kind = 'roadmap_today' | 'reassess'). One per user per UTC day.
  'habit_nudge_sent',
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

export const eventItemSchema = z.object({
  name: z.string().min(1).max(100),
  userId: z.string().optional(),
  sessionId: z.string().optional(),
  payload: z.record(z.string(), z.unknown()).optional().default({}),
  platform: z.enum(['ios', 'android', 'web']).optional(),
  appVersion: z.string().optional(),
  /** ISO-8601 timestamp; defaults to server time if omitted */
  occurredAt: z.string().datetime().optional(),
});

export type EventItem = z.infer<typeof eventItemSchema>;

export const ingestEventsSchema = z.object({
  events: z.array(eventItemSchema).min(1).max(50),
});

export type IngestEventsInput = z.infer<typeof ingestEventsSchema>;

export const ingestEventsResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    accepted: z.number(),
  }),
});

// PRE-76: product-activation funnel. Ordered steps read from `event_log`. The
// first step (waitlist_submitted) is anonymous (keyed by payload.email); every
// later step is keyed by user_id. Conversion is computed step-over-step.
export const FUNNEL_STEPS = [
  'waitlist_submitted',
  'onboarding_started',
  'onboarding_completed',
  'assessment_completed',
  'session_started',
  'session_completed',
  'roadmap_generated',
] as const satisfies readonly EventName[];

export type FunnelStepName = (typeof FUNNEL_STEPS)[number];

export const funnelStepSchema = z.object({
  /** Event name for this step. */
  step: z.string(),
  /** Distinct users (or distinct waitlist emails for the first step) reaching the step. */
  users: z.number(),
  /** users[i] / users[i-1], 0..1; null for the first step. */
  conversionFromPrev: z.number().nullable(),
  /** 1 - conversionFromPrev; null for the first step. */
  dropOffFromPrev: z.number().nullable(),
});

export type FunnelStep = z.infer<typeof funnelStepSchema>;

export const retentionSchema = z.object({
  /** Users with at least one session_started (the retention cohort). */
  cohort: z.number(),
  d1: z.object({ returned: z.number(), rate: z.number() }),
  d7: z.object({ returned: z.number(), rate: z.number() }),
});

export type Retention = z.infer<typeof retentionSchema>;

export const analyticsFunnelResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    window: z.object({
      since: z.string().nullable(),
      until: z.string().nullable(),
    }),
    funnel: z.array(funnelStepSchema),
    retention: retentionSchema,
    generatedAt: z.string(),
  }),
});

export type AnalyticsFunnelResponse = z.infer<typeof analyticsFunnelResponseSchema>;
